// Contacts — unified phonebook (customers + suppliers + trade).
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   Page-section markup     : lines 2679–2700  (#page-contacts)
//   Canonical render IIFE   : lines 17787–17995 — `getTab` / `setContactsTab`,
//                              `loadTrade` / `saveTrade`, `gatherTrade`,
//                              `cardHtml`, `renderContactsPage`,
//                              `openTradeContactModal` / close / save,
//                              `exportContactsCsv`.
//   (The later override at line 39160 — `<tr>` rows inside a `display:grid`
//   container — is broken and is NOT used.)
//   CSS for .ct-* / #trade-modal-* : lines 17541–17574 (ported into styles.css)
//
// Backend (NEW):
//   • Aggregator   GET    /api/portal/contacts[?type=…][&q=…]
//   • Trade-only   GET    /api/portal/trade-contacts
//   • Trade CRUD   POST   /api/portal/trade-contacts
//                  PUT    /api/portal/trade-contacts/:id
//                  DELETE /api/portal/trade-contacts/:id
//   See modules/portal_contacts.js (mounted by server.js).
//
// 2026-05-13 — offline-first layer:
//   Reads + trade-contact mutations go through @tkos/shared-ui's
//   `createRepo` (IndexedDB-first, PG-synced, server-wins conflict).
//   Two stores: `contacts` (full aggregated list, read-only) and
//   `trade_contacts` (only the trade type; mutated by the page). The
//   shared `_sync_queue` survives reloads. A sync-status dot sits in
//   the page toolbar next to the action buttons.
//
// Source-of-truth helpers ported verbatim (renamed only where TS strictness
// requires it):
//   • cardHtml        → buildCardHtml  (inline-styled, includes all SVGs)
//   • gatherTrade     → wired through the contactsRepo; trade-only writes
//                       go through tradeContactsRepo and re-fetch the
//                       aggregator so the list shows the new row instantly
//   • initials / escHtml      → inlined locally (no window globals)
//   • exportContactsCsv → identical column set + CSV escaping
//   • openTradeContactModal / closeTradeContactModal / saveTradeContact →
//     modal builder + handlers, fields verbatim
//
// Departures from source:
//  - All `onclick="…"` attrs swapped for delegated addEventListener handlers.
//  - localStorage swapped for offline-first repo (IndexedDB + PG via queue).
//  - Toast goes through `ctx.showToast` instead of `window.macToast`.
//  - Card-click navigates to the CRM page (`KOSMiniCRM.open(id)` is a
//    deferred extraction; flagged in the task report).
//  - C2C `kosC2cCall` / `openComposeEmail` / `kosOpenCommsForContact` —
//    these still aren't in the modular shell yet. For now Call uses
//    `tel:` href, Email uses `mailto:`, and Msg navigates to messages.
//
// TS strict + exactOptionalPropertyTypes; no window globals.

import { API_BASE } from "@tkos/api-client";
import {
  alertDialog,
  confirmDialog,
  createRepo,
  createSyncDot,
  createSyncQueue,
  createSyncStatus,
  iconMarkup,
  maskEmail,
  maskPhone,
  openDb,
  openMiniCrm,
  type MiniCrmCustomer,
  type Repo,
  type RepoConflictPayload,
  type SyncStatusController,
  type TkosDb,
} from "@tkos/shared-ui";

import { registerPage } from "../../shell/router";
import { customerCache } from "../../shell/customer-cache";
import { openCustomerComposeModal } from "../../shell/customer-compose-modal";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type ContactType = "customer" | "supplier" | "trade";
type TabId = "all" | "customers" | "suppliers" | "trade";

interface ContactAddress {
  line1: string;
  city: string;
  postcode: string;
}

interface Contact {
  id: string;
  name: string;
  subtitle: string;
  phone: string;
  email: string;
  type: ContactType;
  // Customer extras
  stage?: string;
  address?: ContactAddress | null;
  project?: string;
  // Supplier extras
  website?: string;
  logoUrl?: string;
  brandColour?: string;
  // Trade extras
  role?: string;
  company?: string;
  notes?: string;
  createdAt?: number;
  updatedAt?: number;
}

interface TradeContact {
  id: string;
  name: string;
  subtitle: string;
  phone: string;
  email: string;
  type: "trade";
  role: string;
  company: string;
  notes: string;
  createdAt: number;
  updatedAt: number;
}

interface ContactsListResp {
  ok?: boolean;
  retailerId?: string;
  contacts?: Contact[];
}

interface TradeContactResp {
  ok?: boolean;
  retailerId?: string;
  contact?: TradeContact;
}

function isAbortError(err: unknown): boolean {
  return (
    err instanceof DOMException && err.name === "AbortError"
  ) || (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

// Adapt the server's envelopes to the bare records the repos expect.
function parseContactsList(json: unknown): Contact[] {
  const j = json as ContactsListResp | null;
  return Array.isArray(j?.contacts) ? j!.contacts! : [];
}

function parseTradeList(json: unknown): TradeContact[] {
  const j = json as ContactsListResp | null;
  if (!Array.isArray(j?.contacts)) return [];
  // Aggregator/trade-alias both return `Contact` shape; for the trade-only
  // repo every record is type:'trade' so the extra fields are present.
  return (j!.contacts as Contact[]).map((c) => ({
    id:        String(c.id),
    name:      String(c.name || ""),
    subtitle:  String(c.subtitle || ""),
    phone:     String(c.phone || ""),
    email:     String(c.email || ""),
    type:      "trade" as const,
    role:      String(c.role || ""),
    company:   String(c.company || ""),
    notes:     String(c.notes || ""),
    createdAt: typeof c.createdAt === "number" ? c.createdAt : 0,
    updatedAt: typeof c.updatedAt === "number" ? c.updatedAt : 0,
  }));
}

function parseTradeOne(json: unknown): TradeContact | undefined {
  const j = json as TradeContactResp | null;
  if (!j?.contact) return undefined;
  const c = j.contact;
  return {
    id:        String(c.id),
    name:      String(c.name || ""),
    subtitle:  String(c.subtitle || ""),
    phone:     String(c.phone || ""),
    email:     String(c.email || ""),
    type:      "trade",
    role:      String(c.role || ""),
    company:   String(c.company || ""),
    notes:     String(c.notes || ""),
    createdAt: typeof c.createdAt === "number" ? c.createdAt : 0,
    updatedAt: typeof c.updatedAt === "number" ? c.updatedAt : 0,
  };
}

// Raw fetch wrapper for the repo + sync queue — same pattern as Submissions.
function rawApi(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
}

// ── HTML helpers (verbatim source: line 17594/17599) ────────────────────────

function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

function initials(name: unknown): string {
  if (!name) return "?";
  const p = String(name).trim().split(/\s+/);
  const a = ((p[0] ?? "")[0] ?? "").toUpperCase();
  const b = ((p[1] ?? "")[0] ?? "").toUpperCase();
  return (a + b) || "?";
}

function colorFor(type: ContactType): string {
  if (type === "customer") return "#e58200";
  if (type === "supplier") return "#0ea5e9";
  return "#a855f7";
}

function badgeFor(type: ContactType): string {
  if (type === "customer") return '<span class="ct-badge cust">Customer</span>';
  if (type === "supplier") return '<span class="ct-badge sup">Supplier</span>';
  return '<span class="ct-badge trade">Trade</span>';
}

// ── Page markup (verbatim port of source lines 2679–2700) ───────────────────

const PAGE_MARKUP = `
<div class="page-section" id="page-contacts">
  <div style="padding:18px 22px 8px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px">
    <div>
      <h2 style="font-size:20px;font-weight:800;color:var(--text);margin-bottom:6px">Contacts</h2>
      <p style="font-size:12px;color:var(--text2)">Customers, suppliers &amp; trade -- your whole phonebook in one searchable place.</p>
    </div>
    <div id="contacts-toolbar" style="display:flex;gap:8px;align-items:center">
      <button id="contacts-export" class="btn btn-secondary" style="padding:8px 12px;background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer">Export CSV</button>
      <button id="contacts-add-trade" class="btn btn-primary" style="padding:8px 14px;background:#e58200;border:0;color:#fff;border-radius:10px;font-size:12px;font-weight:700;cursor:pointer">+ Add trade contact</button>
    </div>
  </div>
  <div style="padding:0 22px 10px;display:flex;gap:10px;flex-wrap:wrap;align-items:center">
    <input type="search" id="contacts-search" placeholder="Search name, company, phone, email&hellip;" style="flex:1;min-width:240px;background:#1a1a1f;border:1px solid #2a2a2f;color:#eee;border-radius:10px;padding:10px 12px;font-size:13px;outline:none"/>
    <div id="contacts-tabs" style="display:flex;gap:6px;background:#1a1a1f;border:1px solid #2a2a2f;border-radius:10px;padding:4px">
      <button data-tab="all" class="ct-tab ct-tab-active" style="padding:6px 12px;border:0;background:#e58200;color:#fff;border-radius:7px;font-size:11px;font-weight:700;cursor:pointer">All</button>
      <button data-tab="customers" class="ct-tab" style="padding:6px 12px;border:0;background:transparent;color:#aaa;border-radius:7px;font-size:11px;font-weight:700;cursor:pointer">Customers</button>
      <button data-tab="suppliers" class="ct-tab" style="padding:6px 12px;border:0;background:transparent;color:#aaa;border-radius:7px;font-size:11px;font-weight:700;cursor:pointer">Suppliers</button>
      <button data-tab="trade" class="ct-tab" style="padding:6px 12px;border:0;background:transparent;color:#aaa;border-radius:7px;font-size:11px;font-weight:700;cursor:pointer">Trade</button>
    </div>
  </div>
  <div id="contacts-stats" style="padding:0 22px 12px;color:#888;font-size:11px"></div>
  <div id="contacts-list" style="padding:0 22px 28px;display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:12px"></div>
</div>
`;

// Edit-contact modal markup. Mirrors #trade-modal-card styling (the
// platform's existing modal pattern) — same `.tm-head` / `.tm-body` /
// `.tm-actions` / `.tm-btn` class names so the dark-mode tokens just
// work. Fields per the bug fix 2026-05-19: Name, Company, Phone, Email,
// Type. Type is rendered as a disabled select — cross-store moves
// between customer/supplier/trade aren't supported by the existing
// CRM/trade endpoints, so the type field shows the current type for
// context but can't be changed here.
function buildEditModalMarkup(): string {
  return (
    '<div id="edit-contact-modal-overlay" class="ct-edit-overlay">' +
      '<div id="edit-contact-modal-card" class="ct-edit-card">' +
        '<div class="tm-head"><h3>Edit contact</h3>' +
          '<button class="mc-x" id="ec-close" type="button" style="background:transparent;border:0;color:#888;cursor:pointer;font-size:20px">&times;</button>' +
        '</div>' +
        '<div class="tm-body">' +
          '<label><span>Name</span><input type="text" id="ec-name" autocomplete="off" autofocus/></label>' +
          '<label><span>Company</span><input type="text" id="ec-company" autocomplete="off" placeholder="(optional)"/></label>' +
          '<label><span>Phone</span><input type="tel" id="ec-phone" autocomplete="off"/></label>' +
          '<label><span>Email</span><input type="email" id="ec-email" autocomplete="off"/></label>' +
          '<label><span>Type</span>' +
            '<select id="ec-type" disabled title="Type is set when the contact is created and cannot be changed here">' +
              '<option value="customer">Customer</option>' +
              '<option value="supplier">Supplier</option>' +
              '<option value="trade">Trade</option>' +
            '</select>' +
          '</label>' +
        '</div>' +
        '<div class="tm-actions">' +
          '<button class="tm-btn" id="ec-cancel" type="button">Cancel</button>' +
          '<button class="tm-btn primary" id="ec-save" type="button">Save changes</button>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

// Trade contact modal markup (verbatim port of source `openTradeContactModal`,
// line 17944 — but composed as a single string here so the shell can attach
// it once per mount).
function buildTradeModalMarkup(): string {
  return (
    '<div id="trade-modal-overlay">' +
      '<div id="trade-modal-card">' +
        '<div class="tm-head"><h3>Add trade contact</h3>' +
          '<button class="mc-x" id="tm-close" type="button" style="background:transparent;border:0;color:#888;cursor:pointer;font-size:20px">&times;</button>' +
        '</div>' +
        '<div class="tm-body">' +
          '<label><span>Name</span><input type="text" id="tm-name" placeholder="Contact&rsquo;s full name" autofocus/></label>' +
          '<label><span>Role / Trade</span><input type="text" id="tm-role" placeholder="Plumber, electrician, fitter, gasman&hellip;"/></label>' +
          '<label><span>Company</span><input type="text" id="tm-company" placeholder="(optional)"/></label>' +
          '<label><span>Phone</span><input type="tel" id="tm-phone" placeholder="07&hellip;"/></label>' +
          '<label><span>Email</span><input type="email" id="tm-email" placeholder="(optional)"/></label>' +
          '<label><span>Notes</span><textarea id="tm-notes" rows="2" placeholder="Reliable, weekday only, etc."></textarea></label>' +
        '</div>' +
        '<div class="tm-actions">' +
          '<button class="tm-btn" id="tm-cancel" type="button">Cancel</button>' +
          '<button class="tm-btn primary" id="tm-save" type="button">Save contact</button>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

// ── cardHtml — verbatim port of source line 17870 ──────────────────────────

function buildCardHtml(c: Contact): string {
  const phoneClean = String(c.phone || "").replace(/\s/g, "");
  const colour = colorFor(c.type);
  const isCustomer = c.type === "customer";

  // Customer contacts are DEFRAUD-SACRED ([[feedback-contact-masking]]):
  // phone is masked, Call hits the Twilio-masked endpoint (NO tel:),
  // Email hidden in favour of platform messaging (NO mailto:).
  // Trade and supplier contacts are platform-to-platform — they keep
  // tel:/mailto: so the retailer can reach their plumber/electrician
  // directly.
  const actCall = c.phone
    ? (isCustomer
        ? '<button class="ct-act" type="button" data-action="call-masked" data-cid="' +
            esc(c.id) +
          '">' +
            '' + iconMarkup("phone", { size: "sm" }) + 'Call' +
          '</button>'
        : '<a class="ct-act" href="tel:' + esc(phoneClean) + '" data-action="call">' +
            '' + iconMarkup("phone", { size: "sm" }) + 'Call' +
          '</a>')
    : "";

  // Customer cards: Email action removed 2026-05-19 per Steve — Call +
  // Msg are the only two actions on customer cards. Supplier + trade
  // keep the Email action (platform-to-platform contact, mailto: is
  // allowed there).
  const actEmail = (c.email && !isCustomer)
    ? '<a class="ct-act" href="mailto:' + esc(c.email) + '" data-action="email">' +
        '' + iconMarkup("mail", { size: "sm" }) + 'Email' +
      '</a>'
    : "";

  // Msg: source uses `kosOpenCommsForContact`; modular has the Messages page.
  const actMsg =
    '<button class="ct-act" type="button" data-action="msg" data-cid="' + esc(c.id) + '">' +
      '' + iconMarkup("chat", { size: "sm" }) + 'Msg' +
    '</button>';

  // Delete only on trade contacts.
  const del =
    c.type === "trade"
      ? '<button class="ct-act" type="button" data-action="delete-trade" data-id="' +
        esc(c.id) +
        '" style="flex:0 0 auto;padding:7px 10px" title="Delete">' +
          '' + iconMarkup("close", { size: "sm" }) + '' +
        '</button>'
      : "";

  // Hover-visible edit/delete cluster, top-right of every card. The list
  // container gates whether this is shown via `data-edit-allowed="1"`
  // (set after the role check resolves to owner/manager) so the markup
  // is always emitted but the CSS hides it for everyone else.
  // `data-customer-name-skip` exempts this cluster from the shell's
  // capture-phase mini-CRM delegation so the tool buttons reach this page's
  // own click handler deterministically (rather than racing capture). The
  // handler then routes customer Edit/Delete into the mini-CRM explicitly.
  const tools =
    '<div class="ct-card-tools" data-card-tools="1" data-customer-name-skip>' +
      '<button type="button" class="ct-card-tool" data-action="edit-contact"' +
        ' data-cid="' + esc(c.id) + '" data-ctype="' + esc(c.type) + '"' +
        ' aria-label="Edit contact" title="Edit contact">' +
          iconMarkup("pencil", { size: "sm" }) +
      '</button>' +
      '<button type="button" class="ct-card-tool danger" data-action="delete-contact"' +
        ' data-cid="' + esc(c.id) + '" data-ctype="' + esc(c.type) + '"' +
        ' data-cname="' + esc(c.name) + '"' +
        ' aria-label="Delete contact" title="Delete contact">' +
          iconMarkup("trash", { size: "sm" }) +
      '</button>' +
    '</div>';

  return (
    '<div class="ct-card" data-card-id="' + esc(c.id) + '" data-card-type="' + esc(c.type) + '"' +
      (c.type === "customer" ? ' data-customer-name="' + esc(c.name) + '" data-customer-id="' + esc(c.id) + '" style="cursor:pointer"' : "") +
    '>' +
      tools +
      '<div class="ct-head">' +
        '<div class="ct-avatar" style="background:linear-gradient(135deg,' + colour + ',' + colour + 'aa)">' + esc(initials(c.name)) + '</div>' +
        '<div style="flex:1;min-width:0">' +
          '<div class="ct-name">' + esc(c.name) + '</div>' +
          '<div class="ct-meta">' + esc(c.subtitle || "") + '</div>' +
        '</div>' +
        badgeFor(c.type) +
      '</div>' +
      // Body rows — customers get an address line instead of phone/email
      // (per Steve, 2026-05-19): the card surface is just "who they are
      // and where they live". Suppliers and trade keep their phone row
      // because that's the platform-to-platform contact path. Trade
      // contacts also keep their notes row below.
      (c.type === "customer"
        ? (c.address
            ? (() => {
                const a = c.address!;
                const line = [a.city, a.postcode].filter(Boolean).join(", ").trim();
                return line
                  ? '<div class="ct-row">' + iconMarkup("pin", { size: "sm" }) +
                      '<span>' + esc(line) + '</span></div>'
                  : "";
              })()
            : "")
        : (c.phone
            ? '<div class="ct-row">' + iconMarkup("phone", { size: "sm" }) +
                '<span>' + esc(c.phone) + '</span></div>'
            : "")) +
      (c.type === "trade" && c.notes
        ? '<div class="ct-row" style="color:#888;font-style:italic">' + esc(c.notes) + '</div>'
        : "") +
      // `data-customer-name-skip` keeps the capture-phase mini-CRM delegation
      // off the Call/Msg/Email buttons so they fire their own action (masked
      // dial / compose popup) instead of opening the mini-CRM (Bug 8).
      '<div class="ct-actions" data-customer-name-skip>' + actCall + actEmail + actMsg + del + '</div>' +
    '</div>'
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const TAB_ORDER: Record<ContactType, number> = {
  customer: 0,
  supplier: 1,
  trade: 2,
};

const contactsPage: Page = {
  name: "contacts",
  label: "Contacts",
  icon: "📇",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    container.innerHTML = PAGE_MARKUP;
    const root = container.querySelector<HTMLElement>("#page-contacts")!;

    const listEl    = root.querySelector<HTMLElement>("#contacts-list")!;
    const statsEl   = root.querySelector<HTMLElement>("#contacts-stats")!;
    const searchEl  = root.querySelector<HTMLInputElement>("#contacts-search")!;
    const tabsEl    = root.querySelector<HTMLElement>("#contacts-tabs")!;
    const exportBtn = root.querySelector<HTMLButtonElement>("#contacts-export")!;
    const addBtn    = root.querySelector<HTMLButtonElement>("#contacts-add-trade")!;
    const toolbarEl = root.querySelector<HTMLElement>("#contacts-toolbar")!;

    // In-memory caches; truth lives in IndexedDB (mirrored from PG).
    let cache: Contact[] = [];
    let activeTab: TabId = "all";
    let loadState: "loading" | "ready" | "error" = "loading";

    const overlays = new Set<HTMLElement>();
    let modalEl: HTMLElement | null = null;
    let disposed = false;

    // Offline-first wiring.
    let db: TkosDb | null = null;
    let contactsRepo: Repo<Contact> | null = null;
    let tradeRepo: Repo<TradeContact> | null = null;
    let syncStatus: SyncStatusController | null = null;
    const offSubs: Array<() => void> = [];

    const onOnline = (): void => {
      if (contactsRepo) void contactsRepo.list();
      if (tradeRepo) void tradeRepo.list();
    };
    window.addEventListener("online", onOnline);

    // Refresh when a customer is created via the shared add-customer modal
    // (it dispatches `tkos:customer-created` on `window`). Mirrors the CRM
    // pipeline page so the contacts list updates without a manual reload.
    const onCustomerCreated = (): void => {
      void loadAll();
    };
    window.addEventListener("tkos:customer-created", onCustomerCreated);

    // Refresh when a customer is deleted (shell bridges the realtime
    // `contact.deleted` broadcast to this window event) so a deletion in
    // another tab drops the row here without a manual reload.
    const onCustomerDeleted = (): void => {
      void loadAll();
    };
    window.addEventListener("tkos:customer-deleted", onCustomerDeleted);

    // ── Render (verbatim port of source `renderContactsPage`, line 17892) ──
    function render(): void {
      if (disposed) return;
      if (loadState === "loading") {
        statsEl.textContent = "";
        listEl.innerHTML =
          '<div style="grid-column:1/-1;padding:40px;text-align:center;color:#888;font-size:13px">Loading&hellip;</div>';
        return;
      }
      if (loadState === "error") {
        statsEl.textContent = "";
        listEl.innerHTML =
          '<div style="grid-column:1/-1;padding:40px;text-align:center;color:#d33;font-size:13px;background:#0d0d10;border:1px dashed #2a2a2f;border-radius:14px">Could not load contacts. Please refresh the page.</div>';
        return;
      }

      const q = (searchEl.value || "").toLowerCase().trim();

      let rows = cache.slice();
      if (activeTab !== "all") {
        const wanted: ContactType =
          activeTab === "customers"
            ? "customer"
            : activeTab === "suppliers"
              ? "supplier"
              : "trade";
        rows = rows.filter((c) => c.type === wanted);
      }
      if (q) {
        rows = rows.filter((c) => {
          return (
            (c.name     || "").toLowerCase().indexOf(q) >= 0 ||
            (c.subtitle || "").toLowerCase().indexOf(q) >= 0 ||
            (c.phone    || "").toLowerCase().indexOf(q) >= 0 ||
            (c.email    || "").toLowerCase().indexOf(q) >= 0
          );
        });
      }

      // Sort: customers, suppliers, trade — then by name within each.
      rows.sort((a, b) => {
        const oa = TAB_ORDER[a.type] ?? 9;
        const ob = TAB_ORDER[b.type] ?? 9;
        if (oa !== ob) return oa - ob;
        return (a.name || "").localeCompare(b.name || "");
      });

      // Stats line — port of source line 17924 (counts customers / suppliers /
      // trade across the visible result set, matching source plural rules).
      let cN = 0, sN = 0, tN = 0;
      for (const c of rows) {
        if (c.type === "customer") cN++;
        else if (c.type === "supplier") sN++;
        else tN++;
      }
      const total = rows.length;
      statsEl.textContent =
        total +
        " contact" + (total === 1 ? "" : "s") +
        " · " + cN + " customer" + (cN === 1 ? "" : "s") +
        " · " + sN + " supplier" + (sN === 1 ? "" : "s") +
        " · " + tN + " trade";

      if (!rows.length) {
        listEl.innerHTML =
          '<div style="grid-column:1/-1;padding:40px;text-align:center;color:#888;font-size:13px;background:#0d0d10;border:1px dashed #2a2a2f;border-radius:14px">No contacts match.<br/><span style="font-size:11px;color:#666">Try a different search or tab.</span></div>';
      } else {
        listEl.innerHTML = rows.map(buildCardHtml).join("");
      }

      // Restore active tab visual (the inline style + class — both because
      // source toggles both).
      const tabs = tabsEl.querySelectorAll<HTMLButtonElement>(".ct-tab");
      tabs.forEach((b) => {
        const active = b.getAttribute("data-tab") === activeTab;
        b.classList.toggle("ct-tab-active", active);
        b.style.background = active ? "#e58200" : "transparent";
        b.style.color      = active ? "#fff"    : "#aaa";
      });
    }

    // ── Loaders / repo ─────────────────────────────────────────────────────

    async function refreshCacheFromRepo(): Promise<void> {
      if (!contactsRepo) return;
      cache = await contactsRepo.cached();
      // Publish customers into the shared mini-CRM cache so name-clicks
      // anywhere in the app resolve to a full record. Suppliers / trade
      // are intentionally excluded — they're not "customers".
      const customers = cache.filter((c) => c.type === "customer");
      if (customers.length > 0) {
        customerCache.merge(customers.map(contactToMiniCrm));
      }
    }

    async function loadAll(): Promise<void> {
      if (!contactsRepo) return;
      try {
        await contactsRepo.list();
        if (disposed) return;
        await refreshCacheFromRepo();
        loadState = "ready";
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        if (disposed) return;
        console.error("[contacts] load failed", err);
        await refreshCacheFromRepo();
        loadState = cache.length > 0 ? "ready" : "error";
        render();
      }
    }

    // ── Actions ────────────────────────────────────────────────────────────

    async function deleteTradeContact(id: string): Promise<void> {
      if (!tradeRepo) return;
      if (!(await confirmDialog("Remove this trade contact?", {
        title: "Remove trade contact",
        confirmLabel: "Remove",
        destructive: true,
      }))) return;
      try {
        await tradeRepo.remove(id);
        if (disposed) return;
        // Drop from the aggregator cache too so the page reflects it
        // without a round trip.
        cache = cache.filter((c) => c.id !== id);
        render();
        ctx.showToast("Trade contact removed");
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[contacts] delete trade failed", err);
        ctx.showToast("Could not remove contact", "error");
      }
    }

    // ── Edit / Delete (bug fix 2026-05-19) ───────────────────────────────
    //
    // Edit + Delete buttons sit in the top-right of every card and only
    // appear on hover once the role check has set `data-edit-allowed="1"`
    // on the list container (owner / manager only — see ROLE GATING
    // below). Routing per card type:
    //   • customer → PUT/DELETE /api/crm/contacts/:id
    //   • trade    → PUT/DELETE /api/portal/trade-contacts/:id
    //   • supplier → not supported (suppliers are platform-managed and
    //                have no retailer-facing PUT/DELETE endpoint); the
    //                handler toasts to explain.

    let editModalEl: HTMLElement | null = null;
    let editingContact: Contact | null = null;

    function buildEditModal(): HTMLElement {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = buildEditModalMarkup();
      const overlay = wrapper.firstChild as HTMLElement;
      document.body.appendChild(overlay);
      overlays.add(overlay);

      overlay.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).id === "edit-contact-modal-overlay") closeEditModal();
      });
      overlay
        .querySelector<HTMLButtonElement>("#ec-close")
        ?.addEventListener("click", closeEditModal);
      overlay
        .querySelector<HTMLButtonElement>("#ec-cancel")
        ?.addEventListener("click", closeEditModal);
      overlay
        .querySelector<HTMLButtonElement>("#ec-save")
        ?.addEventListener("click", () => { void saveEditedContact(); });
      return overlay;
    }

    function openEditModal(c: Contact): void {
      if (c.type === "supplier") {
        // Suppliers are platform-managed; there's no retailer-facing
        // edit endpoint. Don't open the modal — explain why.
        ctx.showToast(
          "Suppliers are managed by the platform — contact your TKOS admin to update supplier details.",
          "info",
        );
        return;
      }
      if (!editModalEl) editModalEl = buildEditModal();
      editingContact = c;
      const nameInp    = editModalEl.querySelector<HTMLInputElement>("#ec-name");
      const companyInp = editModalEl.querySelector<HTMLInputElement>("#ec-company");
      const phoneInp   = editModalEl.querySelector<HTMLInputElement>("#ec-phone");
      const emailInp   = editModalEl.querySelector<HTMLInputElement>("#ec-email");
      const typeSel    = editModalEl.querySelector<HTMLSelectElement>("#ec-type");
      if (nameInp)    nameInp.value    = c.name;
      if (companyInp) companyInp.value = c.company ?? "";
      if (phoneInp)   phoneInp.value   = c.phone;
      if (emailInp)   emailInp.value   = c.email;
      if (typeSel)    typeSel.value    = c.type;
      // Company isn't a customer field on the CRM side. Hide the input
      // for customer contacts so the modal doesn't promise a save that
      // won't happen.
      const companyLabel = companyInp?.closest("label");
      if (companyLabel) {
        (companyLabel as HTMLElement).style.display = c.type === "customer" ? "none" : "";
      }
      editModalEl.classList.add("show");
      setTimeout(() => nameInp?.focus(), 50);
    }

    function closeEditModal(): void {
      if (editModalEl) editModalEl.classList.remove("show");
      editingContact = null;
    }

    function readEditField(id: string): string {
      const el = editModalEl?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("#" + id);
      return (el?.value || "").trim();
    }

    async function saveEditedContact(): Promise<void> {
      const c = editingContact;
      if (!c) return;
      const name    = readEditField("ec-name");
      const company = readEditField("ec-company");
      const phone   = readEditField("ec-phone");
      const email   = readEditField("ec-email");
      if (!name) {
        await alertDialog("Please enter a name.", { title: "Missing name" });
        editModalEl?.querySelector<HTMLInputElement>("#ec-name")?.focus();
        return;
      }
      const saveBtn = editModalEl?.querySelector<HTMLButtonElement>("#ec-save");
      if (saveBtn) saveBtn.disabled = true;

      // Optimistic update on the in-memory cache so the card refreshes
      // immediately. The repo write below will roll back on failure.
      const prevSnapshot: Contact = { ...c };
      const optimistic: Contact = {
        ...c,
        name,
        phone,
        email,
        subtitle: c.type === "trade"
          ? [c.role, company].filter(Boolean).join(" · ")
          : c.subtitle,
        ...(c.type === "trade" ? { company } : {}),
      };
      const idx = cache.findIndex((x) => x.id === c.id);
      if (idx >= 0) cache[idx] = optimistic;
      render();

      try {
        if (c.type === "customer") {
          // CRM endpoint — sanitiseContact ignores fields it doesn't know.
          const resp = await rawApi(`/crm/contacts/${encodeURIComponent(c.id)}`, {
            method: "PUT",
            body: JSON.stringify({ name, phone, email }),
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        } else if (c.type === "trade") {
          // Trade endpoint — accepts name/phone/email/company/role/notes.
          if (!tradeRepo) throw new Error("Trade repo not initialised");
          await tradeRepo.update(c.id, {
            name,
            phone,
            email,
            company,
          } as Partial<TradeContact>);
        } else {
          // Suppliers shouldn't reach this branch (openEditModal guards
          // them). Defensive: roll back and toast.
          throw new Error("Suppliers cannot be edited from this page");
        }
        closeEditModal();
        ctx.showToast("Contact updated", "success");
      } catch (err) {
        if (isAbortError(err)) {
          if (saveBtn) saveBtn.disabled = false;
          return;
        }
        // Roll back the optimistic update.
        const rollbackIdx = cache.findIndex((x) => x.id === c.id);
        if (rollbackIdx >= 0) cache[rollbackIdx] = prevSnapshot;
        render();
        console.error("[contacts] edit failed", err);
        ctx.showToast("Could not save changes", "error");
      } finally {
        if (saveBtn) saveBtn.disabled = false;
      }
    }

    async function deleteContact(c: Contact): Promise<void> {
      if (c.type === "supplier") {
        ctx.showToast(
          "Suppliers are managed by the platform — contact your TKOS admin to remove a supplier.",
          "info",
        );
        return;
      }
      const ok = await confirmDialog(
        `Are you sure you want to delete ${c.name}? This cannot be undone.`,
        {
          title: "Delete contact",
          confirmLabel: "Delete",
          destructive: true,
        },
      );
      if (!ok) return;

      // Optimistic remove.
      const prevIdx = cache.findIndex((x) => x.id === c.id);
      const snapshot = prevIdx >= 0 ? cache[prevIdx] : null;
      if (prevIdx >= 0) cache.splice(prevIdx, 1);
      render();

      try {
        if (c.type === "customer") {
          const resp = await rawApi(`/crm/contacts/${encodeURIComponent(c.id)}`, {
            method: "DELETE",
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        } else if (c.type === "trade") {
          if (!tradeRepo) throw new Error("Trade repo not initialised");
          await tradeRepo.remove(c.id);
        }
        ctx.showToast("Contact removed", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        // Roll back.
        if (snapshot && prevIdx >= 0) {
          cache.splice(Math.min(prevIdx, cache.length), 0, snapshot);
        }
        render();
        console.error("[contacts] delete failed", err);
        ctx.showToast("Could not remove contact", "error");
      }
    }

    // ── Role gating: owner + manager only ────────────────────────────────
    //
    // Default-deny: the markup ships with the tools cluster, but CSS
    // keeps it hidden unless the list container has data-edit-allowed.
    // Resolve the calling user's role via /api/users/me; if it returns
    // owner or manager, flip the flag.
    void (async (): Promise<void> => {
      try {
        const rid = ctx.user?.retailerId ?? "";
        if (!rid) return;
        interface MeResp { ok?: boolean; user?: { role?: string } | null }
        const resp = await ctx.api<MeResp>(
          `/users/me?retailerId=${encodeURIComponent(rid)}`,
        );
        const role = String(resp?.user?.role ?? "").toLowerCase().trim();
        if (role === "owner" || role === "manager") {
          listEl.setAttribute("data-edit-allowed", "1");
        }
      } catch (_err) {
        // Silent — default-deny is the correct posture if the role
        // check fails (no soft-cookie user yet, 503 db, etc.).
      }
    })();

    // Adapt a Contact -> MiniCrmCustomer for the shared cache.
    function contactToMiniCrm(c: Contact): MiniCrmCustomer {
      const m: MiniCrmCustomer = {
        id:    c.id,
        name:  c.name,
        focusId: c.id,
      };
      if (c.phone)   m.phone = c.phone;
      if (c.email)   m.email = c.email;
      if (c.stage)   m.stage = c.stage;
      if (c.project) m.project = c.project;
      if (c.address) {
        if (c.address.line1)    m.street   = c.address.line1;
        if (c.address.city)     m.town     = c.address.city;
        if (c.address.postcode) m.postcode = c.address.postcode;
      }
      return m;
    }

    // ── Modal ──────────────────────────────────────────────────────────────

    function buildModal(): HTMLElement {
      const w = document.createElement("div");
      w.innerHTML = buildTradeModalMarkup();
      const overlay = w.firstChild as HTMLElement;
      document.body.appendChild(overlay);
      overlays.add(overlay);

      overlay.addEventListener("click", (e) => {
        if ((e.target as HTMLElement).id === "trade-modal-overlay") closeModal();
      });
      overlay
        .querySelector<HTMLButtonElement>("#tm-close")
        ?.addEventListener("click", closeModal);
      overlay
        .querySelector<HTMLButtonElement>("#tm-cancel")
        ?.addEventListener("click", closeModal);
      overlay
        .querySelector<HTMLButtonElement>("#tm-save")
        ?.addEventListener("click", () => { void saveTradeContact(); });
      return overlay;
    }

    function openModal(): void {
      if (!modalEl) modalEl = buildModal();
      const ids = ["tm-name", "tm-role", "tm-company", "tm-phone", "tm-email", "tm-notes"];
      ids.forEach((id) => {
        const el = modalEl?.querySelector<HTMLInputElement | HTMLTextAreaElement>("#" + id);
        if (el) el.value = "";
      });
      modalEl.classList.add("show");
      setTimeout(() => {
        const f = modalEl?.querySelector<HTMLInputElement>("#tm-name");
        if (f) f.focus();
      }, 50);
    }

    function closeModal(): void {
      if (modalEl) modalEl.classList.remove("show");
    }

    function readField(id: string): string {
      const el = modalEl?.querySelector<HTMLInputElement | HTMLTextAreaElement>("#" + id);
      return (el?.value || "").trim();
    }

    async function saveTradeContact(): Promise<void> {
      if (!tradeRepo) return;
      const name = readField("tm-name");
      if (!name) {
        await alertDialog("Please enter a name.", { title: "Missing name" });
        modalEl?.querySelector<HTMLInputElement>("#tm-name")?.focus();
        return;
      }
      const role    = readField("tm-role");
      const company = readField("tm-company");
      const phone   = readField("tm-phone");
      const email   = readField("tm-email");
      const notes   = readField("tm-notes");

      const localId =
        "tc-local-" +
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 8);

      const now = Date.now();
      const record: TradeContact = {
        id:        localId,
        name,
        subtitle:  [role, company].filter(Boolean).join(" · "),
        phone,
        email,
        type:      "trade",
        role,
        company,
        notes,
        createdAt: now,
        updatedAt: now,
      };

      const saveBtn = modalEl?.querySelector<HTMLButtonElement>("#tm-save");
      if (saveBtn) saveBtn.disabled = true;
      try {
        const created = await tradeRepo.create(record);
        if (disposed) return;
        // Unshift into the aggregator cache so the new row shows
        // immediately. When the next /contacts list fires, it'll reconcile.
        const asContact: Contact = {
          id:        created.id,
          name:      created.name,
          subtitle:  created.subtitle,
          phone:     created.phone,
          email:     created.email,
          type:      "trade",
          role:      created.role,
          company:   created.company,
          notes:     created.notes,
          createdAt: created.createdAt,
          updatedAt: created.updatedAt,
        };
        cache = [asContact, ...cache.filter((c) => c.id !== localId && c.id !== created.id)];
        closeModal();
        render();
        ctx.showToast(name + " is in your phonebook", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[contacts] save trade failed", err);
        ctx.showToast("Could not save contact", "error");
      } finally {
        if (saveBtn) saveBtn.disabled = false;
      }
    }

    // ── CSV export (verbatim port of source line 17993) ────────────────────
    //
    // Customer-contact rows are MASKED in the export per
    // [[feedback-contact-masking]]: retailer CSV downloads must not leak
    // raw phone/email. Trade and supplier contacts are platform-to-
    // platform and remain unmasked. Admin-portal export (when built) gets
    // a separate unmasked path via the x-admin-portal header.

    function exportContactsCsv(): void {
      const rows: string[][] = [["Type", "Name", "Subtitle", "Phone", "Email"]];
      cache.forEach((c) => {
        const isCustomer = c.type === "customer";
        const phoneOut = isCustomer ? maskPhone(c.phone) : (c.phone || "");
        const emailOut = isCustomer ? maskEmail(c.email) : (c.email || "");
        // maskPhone/maskEmail return "—" for absent values — coerce to empty
        // string for CSV so an absent field is just "" rather than the dash.
        rows.push([
          c.type,
          c.name,
          c.subtitle || "",
          phoneOut === "—" ? "" : phoneOut,
          emailOut === "—" ? "" : emailOut,
        ]);
      });
      const csv = rows
        .map((r) =>
          r
            .map((v) => {
              const s = String(v == null ? "" : v);
              return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
            })
            .join(","),
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kitchenos-contacts-" + new Date().toISOString().slice(0, 10) + ".csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      ctx.showToast((rows.length - 1) + " rows exported");
    }

    // ── Event wiring ───────────────────────────────────────────────────────

    searchEl.addEventListener("input", render);

    tabsEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-tab]");
      if (!btn) return;
      const t = btn.getAttribute("data-tab") as TabId | null;
      if (!t) return;
      activeTab = t;
      render();
    });

    exportBtn.addEventListener("click", exportContactsCsv);
    addBtn.addEventListener("click", openModal);

    // Open the mini-CRM record popup for a customer contact — the same target
    // a card-body / name click resolves to. Mirrors the shell's
    // `onViewFullRecord` wiring (navigate to the full customer record).
    function openContactMiniCrm(c: Contact): void {
      const customer = customerCache.lookup(c.name, c.id) ?? contactToMiniCrm(c);
      openMiniCrm({
        customer,
        onViewFullRecord: () => ctx.navigate("customer", { id: c.id }),
      });
    }

    // Delegated card actions. Card-body clicks reach the shell's capture-phase
    // mini-CRM `data-customer-name` delegation. The Edit/Delete tool cluster is
    // marked `data-customer-name-skip`, so those button clicks fall through to
    // this handler: customer Edit/Delete open the mini-CRM record; trade
    // contacts (no record) keep their inline edit/delete. Other inline actions
    // (msg / call / email) fire their own behaviour.
    listEl.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Action button inside a card.
      const actBtn = target.closest<HTMLElement>("[data-action]");
      if (actBtn) {
        const action = actBtn.getAttribute("data-action");
        if (action === "edit-contact") {
          e.preventDefault();
          e.stopPropagation();
          const id = actBtn.getAttribute("data-cid") || "";
          const c = cache.find((x) => x.id === id);
          if (!c) return;
          // Customers open the mini-CRM record popup (same target as a
          // card-body / name click). Trade contacts have no mini-CRM record,
          // so they keep the inline edit modal.
          if (c.type === "customer") {
            openContactMiniCrm(c);
          } else {
            openEditModal(c);
          }
          return;
        }
        if (action === "delete-contact") {
          e.preventDefault();
          e.stopPropagation();
          const id = actBtn.getAttribute("data-cid") || "";
          const c = cache.find((x) => x.id === id);
          if (!c) return;
          // Customers open the mini-CRM record (deletion lives inside the
          // record). Trade contacts keep the inline delete action.
          if (c.type === "customer") {
            openContactMiniCrm(c);
          } else {
            void deleteContact(c);
          }
          return;
        }
        if (action === "delete-trade") {
          e.preventDefault();
          e.stopPropagation();
          const id = actBtn.getAttribute("data-id") || "";
          if (id) void deleteTradeContact(id);
          return;
        }
        if (action === "msg" || action === "email-platform") {
          e.preventDefault();
          e.stopPropagation();
          const cid = actBtn.getAttribute("data-cid") || "";
          const c = cid ? cache.find((x) => x.id === cid) : null;
          // Customers → in-place compose popup (retailer stays on the page),
          // same as the customer record. Trade/supplier keep the full
          // Messages page. (Bug 8)
          if (c && c.type === "customer") {
            const ownRetailerId =
              (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
              ctx.user?.retailerId ?? "";
            const retailerName = ctx.user?.retailerName ?? "Retailer";
            openCustomerComposeModal(
              {
                customer: { id: c.id, name: c.name, email: c.email ?? undefined },
                ownRetailerId,
                retailerName,
              },
              ctx,
            );
          } else {
            ctx.navigate("messages");
          }
          return;
        }
        if (action === "call-masked") {
          // DEFRAUD-SACRED: never `tel:` ([[feedback-contact-masking]]).
          // Hit the Twilio-masked dial endpoint; stub returns 503 until
          // Phase 3 — toast accordingly.
          e.preventDefault();
          e.stopPropagation();
          const cid = actBtn.getAttribute("data-cid") || "";
          if (!cid) return;
          void (async () => {
            try {
              await ctx.api<{ ok: boolean }>("/portal/calls/dial", {
                method: "POST",
                body: JSON.stringify({ customerId: cid }),
              });
              ctx.showToast("Connecting call…", "success");
            } catch (err) {
              const status = (err as { status?: number } | null)?.status;
              if (status === 503) {
                ctx.showToast("Click-to-call goes live with Twilio (Phase 3).", "info");
              } else {
                ctx.showToast("Could not start call", "error");
              }
            }
          })();
          return;
        }
        // call / email on supplier/trade — anchor tags with tel:/mailto:.
        // Let the browser handle them. Stop bubbling so the shell's
        // mini-CRM delegation doesn't also fire.
        e.stopPropagation();
        return;
      }
      // Else: card-body click → shell's mini-CRM delegation handles it
      // via the `data-customer-name` attribute on `.ct-card`.
    });

    // ── Offline-first init ─────────────────────────────────────────────────

    async function initOffline(): Promise<void> {
      try {
        db = await openDb(["contacts", "trade_contacts", "_sync_queue"]);
        if (disposed) return;
        const queue = createSyncQueue(db);
        syncStatus = createSyncStatus(queue);

        // Read-only aggregator repo. We don't expose create/update/remove
        // semantics for it on the UI — but the underlying repo still
        // supports them (no mutations land here in practice).
        contactsRepo = createRepo<Contact>({
          store:     "contacts",
          endpoint:  "/portal/contacts",
          api:       rawApi,
          db,
          queue,
          parseList: parseContactsList,
          // The aggregator does not expose POST/PUT/DELETE, so parseOne is
          // never reached for `contacts` — return undefined to be safe.
          parseOne:  () => undefined,
        });

        // Mutating repo for trade only.
        tradeRepo = createRepo<TradeContact>({
          store:     "trade_contacts",
          endpoint:  "/portal/trade-contacts",
          api:       rawApi,
          db,
          queue,
          parseList: parseTradeList,
          parseOne:  parseTradeOne,
        });

        offSubs.push(
          contactsRepo.on("change", () => {
            void (async (): Promise<void> => {
              if (disposed || !contactsRepo) return;
              await refreshCacheFromRepo();
              if (loadState === "ready") render();
            })();
          }),
        );

        offSubs.push(
          tradeRepo.on("change", () => {
            // A trade write may have landed; refresh the aggregator so
            // the merged list reflects truth.
            void (async (): Promise<void> => {
              if (disposed || !contactsRepo) return;
              try {
                await contactsRepo.list();
                await refreshCacheFromRepo();
                if (loadState === "ready") render();
              } catch (err) {
                if (!isAbortError(err)) {
                  console.error("[contacts] tradeRepo->aggregator refresh failed", err);
                }
              }
            })();
          }),
        );

        offSubs.push(
          tradeRepo.on("conflict", (...args: unknown[]) => {
            const payload = args[0] as RepoConflictPayload<TradeContact> | undefined;
            const sv = payload?.serverVersion;
            const who = sv?.name ?? "Trade contact";
            ctx.showToast(
              who + " updated on another device — server version applied",
              "info",
            );
          }),
        );

        // Mount the sync dot in the toolbar, just before "+ Add trade
        // contact". Margins mirror the Submissions placement.
        const dot = createSyncDot({ status: syncStatus });
        dot.el.style.marginLeft = "4px";
        dot.el.style.marginRight = "4px";
        toolbarEl.insertBefore(dot.el, addBtn);
        offSubs.push(() => dot.destroy());

        // Drive the syncing-dot while the initial list runs.
        const wrappedList = (async (): Promise<void> => {
          syncStatus?.setDraining(true);
          try {
            await loadAll();
          } finally {
            syncStatus?.setDraining(false);
          }
        });
        void wrappedList();
      } catch (err) {
        console.error("[contacts] offline init failed", err);
        loadState = "error";
        render();
      }
    }

    render();
    void initOffline();

    return (): void => {
      disposed = true;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("tkos:customer-created", onCustomerCreated);
      window.removeEventListener("tkos:customer-deleted", onCustomerDeleted);
      for (const off of offSubs) {
        try {
          off();
        } catch (err) {
          console.error("[contacts] cleanup listener threw", err);
        }
      }
      offSubs.length = 0;
      syncStatus?.destroy();
      syncStatus = null;
      contactsRepo = null;
      tradeRepo = null;
      db = null;
      for (const ov of overlays) ov.remove();
      overlays.clear();
      modalEl = null;
      editModalEl = null;
      editingContact = null;
    };
  },
};

registerPage(contactsPage);

export default contactsPage;
